// Unified tasks view for both operators and managers
// CHANGE: Removed all debug console statements for production
// CREATED: Unified component that replaces TodayView and ManagerTodayView
// Shows personal tasks with calendar selection, grouping by period, progress tracking, and celebration
// UPDATED: Added responsive padding adjustments for desktop layout
// UPDATED: Added sidebar toggle button for desktop view
// UPDATED: Progress bar and encouragement messages only show for today's date
//          Past dates show completion summary, future dates show planned tasks count
// UPDATED: Removed internal padding - now relies on UnifiedView's uniform px-8 py-8 padding for consistency with admin interface
// UPDATED: Implemented Google Agenda style inline calendar with Today button, smooth animations, and auto-close on scroll
// UPDATED: Restructured mobile header for better UX:
//          - Greeting (text-base font-medium text-gray-600) and first name (text-lg font-semibold text-gray-800) on separate lines (left side)
//          - First name properly capitalized (handles compound names like Jean-Pierre)
//          - Date selector aligned to right, vertically centered
//          - Removed "Aujourd'hui" button for cleaner, simpler interface
//          - Maximized vertical space by consolidating into single horizontal layout
// UPDATED: Mobile header now has white background with bottom border separator matching bottom nav styling

import { useState, useEffect, useRef } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, Trophy, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { getAssignedTasksForDate, AssignedTaskWithDetails } from '../../lib/execution';
import { formatDate } from '../../lib/dateUtils';
import { TaskCard } from '../operator/TaskCard';
import { TaskExecutionView } from '../operator/TaskExecutionView';
import { TaskProgressBar } from '../ui/TaskProgressBar';
import { InlineMonthCalendar } from '../ui/InlineMonthCalendar';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { getCachedTasksForDate, cacheTasksForDate, cacheUserInfo } from '../../lib/offlineCache';
import { capitalizeFirstName } from '../../lib/nameUtils';

export function MyTasksView() {
  const { user } = useAuth();
  const { isDesktop } = useResponsiveLayout();
  const { isOnline } = useNetworkStatus();
  const [tasks, setTasks] = useState<AssignedTaskWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(formatDate(new Date()));
  const [showCalendar, setShowCalendar] = useState(false);
  const [showBounce, setShowBounce] = useState(false);
  const [isUsingCache, setIsUsingCache] = useState(false);
  const prevCompletedRef = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTasks();
  }, [user, currentDate]);

  const loadTasks = async () => {
    if (!user) return;

    try {
      // Only show loading spinner if we haven't loaded tasks yet
      // This prevents the loader from appearing on tab switches
      if (tasks.length === 0) {
        setLoading(true);
      }
      setError(null);
      setIsUsingCache(false);

      if (!isOnline) {
        // Try to load from cache when offline
        const cachedTasks = getCachedTasksForDate(user.id, currentDate);
        if (cachedTasks) {
          const sortedTasks = cachedTasks.sort((a, b) => {
            const timeA = a.scheduled_time || '00:00:00';
            const timeB = b.scheduled_time || '00:00:00';
            return timeA.localeCompare(timeB);
          });
          setTasks(sortedTasks);
          setIsUsingCache(true);
        } else {
          setTasks([]);
          setError('Aucune donnée hors ligne disponible pour cette date');
        }
      } else {
        // Online: fetch from API
        const data = await getAssignedTasksForDate(user.id, currentDate);

        const sortedTasks = data.sort((a, b) => {
          const timeA = a.scheduled_time || '00:00:00';
          const timeB = b.scheduled_time || '00:00:00';
          return timeA.localeCompare(timeB);
        });

        setTasks(sortedTasks);

        // Cache the data for offline use
        cacheTasksForDate(user.id, currentDate, sortedTasks);

        // Cache user info
        if (user.first_name || user.last_name || user.email) {
          cacheUserInfo(user.id, {
            firstName: user.first_name,
            lastName: user.last_name,
            email: user.email,
            role: user.role
          });
        }
      }
    } catch (err) {
      // If online request fails, try cache as fallback
      if (isOnline) {
        const cachedTasks = getCachedTasksForDate(user.id, currentDate);
        if (cachedTasks) {
          setTasks(cachedTasks);
          setIsUsingCache(true);
          setError('Chargement depuis le cache (connexion instable)');
        } else {
          setError('Erreur lors du chargement des missions');
        }
      } else {
        setError('Aucune donnée hors ligne disponible');
      }
    } finally {
      if (tasks.length === 0) {
        setLoading(false);
      }
    }
  };

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
  };

  const handleBackToList = () => {
    setSelectedTaskId(null);
    loadTasks();
  };

  const allTasksCompleted = tasks.length > 0 && tasks.every(t => t.status === 'completed');

  useEffect(() => {
    if (tasks.length > 0) {
      const currentCompleted = tasks.filter(t => t.status === 'completed').length;
      if (currentCompleted === tasks.length && prevCompletedRef.current < tasks.length) {
        setShowBounce(true);
        setTimeout(() => setShowBounce(false), 1000);
      }
      prevCompletedRef.current = currentCompleted;
    }
  }, [tasks]);

  // Auto-close calendar on scroll down only (Google Agenda behavior)
  useEffect(() => {
    if (!showCalendar || !contentRef.current) return;

    let lastScrollTop = 0;
    let accumulatedDeltaY = 0;
    const scrollThreshold = 5; // Minimum scroll distance to trigger close (for touch)
    const wheelThreshold = 30; // Minimum wheel delta to trigger close (for mouse wheel)

    // Handle touch/trackpad scroll
    const handleScroll = () => {
      if (!contentRef.current) return;

      const scrollTop = contentRef.current.scrollTop;
      const scrollDiff = scrollTop - lastScrollTop;

      // Close calendar only if user scrolls DOWN more than threshold pixels
      if (scrollDiff > scrollThreshold && showCalendar) {
        setShowCalendar(false);
      }

      lastScrollTop = scrollTop;
    };

    // Handle mouse wheel
    const handleWheel = (e: WheelEvent) => {
      // Only accumulate downward scroll (positive deltaY)
      if (e.deltaY > 0) {
        accumulatedDeltaY += e.deltaY;

        // Close calendar if accumulated downward wheel movement exceeds threshold
        if (accumulatedDeltaY > wheelThreshold && showCalendar) {
          setShowCalendar(false);
          accumulatedDeltaY = 0;
        }
      } else {
        // Reset counter on upward scroll
        accumulatedDeltaY = 0;
      }
    };

    // Reset accumulated delta on mouse wheel stop (debounced)
    let wheelTimeout: NodeJS.Timeout;
    const handleWheelDebounced = (e: WheelEvent) => {
      clearTimeout(wheelTimeout);
      handleWheel(e);
      wheelTimeout = setTimeout(() => {
        accumulatedDeltaY = 0;
      }, 150);
    };

    const contentElement = contentRef.current;
    const calendarElement = calendarRef.current;

    // Listen to scroll and wheel on content area
    contentElement.addEventListener('scroll', handleScroll, { passive: true });
    contentElement.addEventListener('wheel', handleWheelDebounced, { passive: true });

    // Also listen to wheel on calendar itself
    if (calendarElement) {
      calendarElement.addEventListener('wheel', handleWheelDebounced, { passive: true });
    }

    return () => {
      clearTimeout(wheelTimeout);
      contentElement.removeEventListener('scroll', handleScroll);
      contentElement.removeEventListener('wheel', handleWheelDebounced);
      if (calendarElement) {
        calendarElement.removeEventListener('wheel', handleWheelDebounced);
      }
    };
  }, [showCalendar]);

  if (selectedTaskId) {
    return (
      <TaskExecutionView
        taskId={selectedTaskId}
        onBack={handleBackToList}
      />
    );
  }

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length
  };

  // Check if selected date is today
  const todayStr = formatDate(new Date());
  const isToday = currentDate === todayStr;
  const isPast = currentDate < todayStr;


  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return 'Bonjour';
    } else if (hour >= 12 && hour < 18) {
      return 'Bon après-midi';
    } else {
      return 'Bonsoir';
    }
  };

  const getFormattedFirstName = () => {
    const firstName = user?.first_name || user?.email?.split('@')[0] || 'vous';
    return capitalizeFirstName(firstName);
  };


  const handleDateSelect = (date: string) => {
    setCurrentDate(date);
    setShowCalendar(false);
  };

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  if (!user) return null;

  return (
    <div className="flex flex-col h-full pb-24 lg:pb-8 pt-[env(safe-area-inset-top)]">
      {/* Fixed header with date selector and Today button */}
      <div className={`flex-shrink-0 mb-4 ${!isDesktop ? 'bg-white border-b border-gray-200 -mx-4 px-4 pb-4' : ''}`}>
        <div className="flex items-center justify-between gap-4">
          {/* Left side: Greeting and Name */}
          <div className="flex items-center gap-3">
            <div>
              <p className="text-base font-medium text-gray-600">
                {getGreeting()}
              </p>
              <h1 className="text-lg font-semibold text-gray-800">
                {getFormattedFirstName()}
              </h1>
            </div>
          </div>

          {/* Right side: Date selector */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleCalendar}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-all duration-200 shadow-sm active:scale-95"
              aria-expanded={showCalendar}
              aria-label="Sélectionner une date"
            >
              <Calendar size={16} className="text-gray-700" />
              <span className="font-medium text-gray-900">
                {new Date(currentDate).toLocaleDateString('fr-FR', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}
              </span>
              {showCalendar ? (
                <ChevronUp size={16} className="text-gray-700" />
              ) : (
                <ChevronDown size={16} className="text-gray-700" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Inline calendar - expands/collapses smoothly */}
      <div ref={calendarRef}>
        <InlineMonthCalendar
          selectedDate={currentDate}
          onSelectDate={handleDateSelect}
          isOpen={showCalendar}
        />
      </div>

      {/* Scrollable content area */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto"
        style={{
          scrollBehavior: 'smooth',
          overscrollBehavior: 'contain'
        }}
      >
        {/* Offline mode notice */}
        {isUsingCache && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={18} className="text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-orange-800">Mode hors ligne</p>
              <p className="text-xs text-orange-700 mt-0.5">Données affichées depuis le cache local. Les modifications seront synchronisées à la reconnexion.</p>
            </div>
          </div>
        )}

        {/* Progress bar - only show for today */}
        {isToday && stats.total > 0 && (
          <div className="mb-4 p-4 rounded-xl bg-blue-50 transition-all duration-500">
            <TaskProgressBar
              completed={stats.completed}
              total={stats.total}
              size="md"
              animated={true}
            />
          </div>
        )}

        {/* Summary for past dates */}
        {isPast && stats.total > 0 && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700">
              Résumé : <span className="font-medium">{stats.completed}</span> mission{stats.completed > 1 ? 's' : ''} complétée{stats.completed > 1 ? 's' : ''} / <span className="font-medium">{stats.total}</span> assignée{stats.total > 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Celebration banner - only for today */}
        {isToday && allTasksCompleted && (
          <div className={`mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm transition-transform duration-500 ${
            showBounce ? 'animate-bounce-subtle' : ''
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="text-green-600" size={20} />
              <h2 className="text-base font-bold text-green-800">
                Félicitations !
              </h2>
            </div>
            <p className="text-green-700 text-sm">
              Toutes vos missions du jour sont terminées. Excellent travail !
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <BouncingDotsLoader size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
            <p className="text-gray-600">{error}</p>
            <button
              onClick={loadTasks}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Réessayer
            </button>
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
            <CheckCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600">Aucune mission assignée pour cette date</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={() => handleTaskClick(task.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
